import { useReducer } from 'react'
import { FileSpreadsheet } from 'lucide-react'
import { wizardReducer, initialWizardState, WizardStep } from './wizardState'
import { UploadStep } from './steps/UploadStep'
import { MapColumnsStep } from './steps/MapColumnsStep'
import { ValidateStep } from './steps/ValidateStep'
import { ReviewImportStep } from './steps/ReviewImportStep'
import { StepperBar } from './steps/StepperBar'

// ---------------------------------------------------------------------------
// Step metadata
// ---------------------------------------------------------------------------

const STEP_TITLES: Record<WizardStep, { title: string; subtitle: string }> = {
  upload: {
    title: 'Upload CSV',
    subtitle: 'Import contacts from a CSV file.',
  },
  map: {
    title: 'Map Columns',
    subtitle: 'Match your CSV columns to First Bite contact fields.',
  },
  validate: {
    title: 'Validate Data',
    subtitle: 'Review and fix any issues before importing.',
  },
  review: {
    title: 'Review & Import',
    subtitle: 'Review your import and add tags before finishing.',
  },
}

// ---------------------------------------------------------------------------
// Wizard shell
// ---------------------------------------------------------------------------

export function ContactImportWizard() {
  const [state, dispatch] = useReducer(wizardReducer, initialWizardState)
  const { step, file } = state
  const stepInfo = STEP_TITLES[step]

  return (
    <div className="h-screen bg-bg-secondary py-8 px-12 flex flex-col">
      <div className="w-full flex flex-col flex-1 min-h-0">
        {/* Wizard card */}
        <div className="bg-surface rounded-2xl shadow-sm border border-line flex flex-col flex-1 min-h-0 overflow-hidden">
          {/* Stepper */}
          <div className="px-8 py-5 border-b border-line">
            <StepperBar
              currentStep={step}
              onStepClick={(target) =>
                dispatch({ type: 'SET_STEP', step: target })
              }
            />
          </div>

          {/* Step header */}
          <div className="px-8 pt-6 pb-4 border-b border-line">
            <div className="flex items-center gap-2.5">
              <h2 className="text-lg font-bold text-fg">{stepInfo.title}</h2>
              {file && step !== 'upload' && (
                <>
                  <span className="text-fg-muted">·</span>
                  <span className="flex items-center gap-1 text-xs text-fg-muted whitespace-nowrap truncate max-w-[200px]">
                    <FileSpreadsheet className="size-3 shrink-0" />
                    {file.name}
                  </span>
                </>
              )}
            </div>
            <p className="text-sm text-fg-secondary mt-0.5">
              {stepInfo.subtitle}
            </p>
          </div>

          {/* Step content */}
          <div className="flex-1 relative overflow-hidden">
            {step === 'upload' && (
              <UploadStep state={state} dispatch={dispatch} />
            )}

            {step === 'map' && (
              <MapColumnsStep state={state} dispatch={dispatch} />
            )}

            {step === 'validate' && (
              <ValidateStep state={state} dispatch={dispatch} />
            )}

            {step === 'review' && (
              <ReviewImportStep state={state} dispatch={dispatch} />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
